import React, { useState, useEffect, useCallback } from 'react';
import { BeakerIcon, SparklesIcon, CheckIcon, XMarkIcon, EyeIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import templateService, { EmailTemplate } from '../../services/templateService';
import { voiceSettingsService } from '../../services/voiceSettingsService';
import TemplatePreview from '../Templates/TemplatePreview';

interface Segment {
  id: number;
  name: string;
  description: string;
  active_leads: number;
}

interface VoiceSettings {
  tone: string;
  perspective: string;
  style: string;
  avoid_terms: string[];
  include_elements: string[];
  preferred_length: string;
  cta_style: string;
}

interface GenerationResult {
  campaign_id: number;
  segment_name: string;
  subject: string;
  preview: string;
}

interface AvailableTemplate {
  id: number;
  name: string;
  description: string;
  category: string;
  usage_count: number;
  variables_schema: any[] | null;
}


interface ContentGeneratorProps {
  onClose: () => void;
  onGenerated: (results: GenerationResult[]) => void;
}

const ContentGenerator: React.FC<ContentGeneratorProps> = ({ onClose, onGenerated }) => {
  const [segments, setSegments] = useState<Segment[]>([]);
  const [selectedSegments, setSelectedSegments] = useState<number[]>([]);
  const [prompt, setPrompt] = useState('');
  const [theme, setTheme] = useState('');
  const [voiceSettings, setVoiceSettings] = useState<VoiceSettings | null>(null);
  const [includeResources, setIncludeResources] = useState(true);
  const [useKnowledgeBase, setUseKnowledgeBase] = useState(true);
  const [campaignMode, setCampaignMode] = useState<'individual' | 'single'>('single');
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  
  // Segment compatibility state
  const [segmentCompatibility, setSegmentCompatibility] = useState<{
    compatible: boolean;
    conflicts: string[];
    recommendation: string;
    safetyLevel: 'safe' | 'warning' | 'risky';
    segmentCount: number;
    segmentNames: string[];
  } | null>(null);
  const [checkingCompatibility, setCheckingCompatibility] = useState(false);
  
  // Template-related state
  const [useTemplate, setUseTemplate] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<number | null>(null);
  const [availableTemplates, setAvailableTemplates] = useState<AvailableTemplate[]>([]);
  const [templateCategory, setTemplateCategory] = useState<string>('');
  
  // Template preview state
  const [previewTemplate, setPreviewTemplate] = useState<EmailTemplate | null>(null);
  const [showTemplatePreview, setShowTemplatePreview] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  const loadSegments = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/segments`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('chatbot_admin_token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load segments');
      }

      const data = await response.json();
      setSegments(data.filter((segment: Segment) => segment.active_leads > 0));
    } catch (err) {
      console.error('Error loading segments:', err);
      setError('Failed to load segments');
    }
  }, [API_BASE_URL]);

  const loadAvailableTemplates = useCallback(async () => {
    try {
      const templates = await templateService.getAvailableTemplatesForCampaigns(templateCategory || undefined);
      setAvailableTemplates(templates);

      // Auto-select the first template if none selected and templates are available
      if (!selectedTemplate && templates.length > 0) {
        setSelectedTemplate(templates[0].id);
      }
    } catch (err) {
      console.error('Error loading templates:', err);
      // Don't show error to user as templates are optional
    }
  }, [templateCategory, selectedTemplate]);

  useEffect(() => {
    loadSegments();
    loadDefaultVoiceSettings();
    loadAvailableTemplates();
  }, [loadSegments, loadAvailableTemplates]);

  // Load templates when category changes
  useEffect(() => {
    loadAvailableTemplates();
  }, [templateCategory, loadAvailableTemplates]);

  const loadDefaultVoiceSettings = async () => {
    try {
      console.log('🎤 ContentGenerator: Loading default voice settings from API...');
      const defaultSettings = await voiceSettingsService.getDefaultVoiceSetting();

      console.log('✅ ContentGenerator: Received default voice settings:', defaultSettings);

      // Convert the database format to the component format
      setVoiceSettings({
        tone: defaultSettings.tone,
        perspective: defaultSettings.perspective,
        style: defaultSettings.style,
        avoid_terms: defaultSettings.avoid_terms,
        include_elements: defaultSettings.include_elements,
        preferred_length: defaultSettings.preferred_length,
        cta_style: defaultSettings.cta_style
      });
    } catch (err) {
      console.error('❌ ContentGenerator: Error loading voice settings:', err);

      // Fallback to hardcoded defaults if API fails
      console.log('🔄 ContentGenerator: Using fallback defaults');
      setVoiceSettings({
        tone: 'warm, supportive, professional',
        perspective: 'understanding parent community',
        style: 'conversational but authoritative',
        avoid_terms: ['medical advice', 'overwhelming jargon', 'guarantees'],
        include_elements: ['practical tips', 'community support', 'next steps'],
        preferred_length: '300-500 words',
        cta_style: 'gentle encouragement'
      });
    }
  };

  const handleTemplatePreview = async (templateId: number) => {
    try {
      const template = await templateService.getTemplate(templateId);
      setPreviewTemplate(template);
      setShowTemplatePreview(true);
    } catch (err) {
      console.error('Error loading template for preview:', err);
      // Could show error to user if needed
    }
  };

  const handleSegmentToggle = (segmentId: number) => {
    setSelectedSegments(prev => {
      const newSelected = prev.includes(segmentId) 
        ? prev.filter(id => id !== segmentId)
        : [...prev, segmentId];
      
      // Check compatibility when segments change (but only in single campaign mode)
      if (campaignMode === 'single' && newSelected.length > 1) {
        checkSegmentCompatibility(newSelected);
      } else {
        setSegmentCompatibility(null);
      }
      
      return newSelected;
    });
  };
  
  const checkSegmentCompatibility = async (segmentIds: number[]) => {
    if (segmentIds.length <= 1) {
      setSegmentCompatibility(null);
      return;
    }

    try {
      setCheckingCompatibility(true);
      const token = localStorage.getItem('chatbot_admin_token');
      
      const response = await fetch(`${API_BASE_URL}/api/campaigns/check-segment-compatibility`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ segment_ids: segmentIds })
      });

      if (!response.ok) {
        throw new Error('Failed to check segment compatibility');
      }

      const data = await response.json();
      setSegmentCompatibility(data.compatibility);
      
    } catch (err) {
      console.error('Error checking segment compatibility:', err);
      // Don't show error to user for this check
    } finally {
      setCheckingCompatibility(false);
    }
  };

  const selectAllSegments = () => {
    setSelectedSegments(segments.map(s => s.id));
  };

  const clearAllSegments = () => {
    setSelectedSegments([]);
    setSegmentCompatibility(null);
  };

  // Check compatibility when campaign mode changes
  const handleCampaignModeChange = (mode: 'individual' | 'single') => {
    setCampaignMode(mode);
    setSegmentCompatibility(null);
    
    if (mode === 'single' && selectedSegments.length > 1) {
      checkSegmentCompatibility(selectedSegments);
    }
  };

  const handleGenerate = async () => {
    if (selectedSegments.length === 0) {
      setError('Please select at least one segment');
      return;
    }

    if (!prompt.trim()) {
      setError('Please provide a content prompt');
      return;
    }

    try {
      setGenerating(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}/api/campaigns/generate-content`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('chatbot_admin_token')}`
        },
        body: JSON.stringify({
          segment_ids: selectedSegments,
          prompt: prompt.trim(),
          theme: theme.trim() || undefined,
          voice_settings: voiceSettings,
          include_resources: includeResources,
          use_knowledge_base: useKnowledgeBase,
          campaign_mode: campaignMode,
          template_id: useTemplate ? selectedTemplate : undefined,
          use_template: useTemplate
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate content');
      }

      const data = await response.json();
      
      console.log('✅ Content generation successful:', data);
      onGenerated(data.results);
      onClose();

    } catch (err) {
      console.error('Content generation error:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate content');
    } finally {
      setGenerating(false);
    }
  };

  const promptSuggestions = [
    'Weekly newsletter with upcoming events and resources',
    'Back-to-school preparation tips and checklist',
    'Summer program opportunities and registration info',
    'IEP and 504 plan guidance for new families',
    'Sensory-friendly activities and strategies',
    'Parent education workshop announcements',
    'School tour scheduling and enrollment updates',
    'Holiday break activities and resources',
    'Assessment results interpretation guide',
    'Community resources and support groups'
  ];

  const totalRecipients = selectedSegments.reduce((total, segmentId) => {
    const segment = segments.find(s => s.id === segmentId);
    return total + (segment?.active_leads || 0);
  }, 0);

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[95vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center">
            <BeakerIcon className="h-6 w-6 text-purple-600 mr-3" />
            <h3 className="text-lg font-medium text-gray-900">
              AI Content Generator
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto scroll-smooth">
          <div className="p-6 space-y-6">
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Error</h3>
                  <div className="mt-2 text-sm text-red-700">{error}</div>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-6">
            {/* Segment Selection */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  Select Segments ({selectedSegments.length} selected)
                </label>
                <div className="space-x-2">
                  <button
                    onClick={selectAllSegments}
                    className="text-sm text-indigo-600 hover:text-indigo-800"
                  >
                    Select All
                  </button>
                  <button
                    onClick={clearAllSegments}
                    className="text-sm text-gray-600 hover:text-gray-800"
                  >
                    Clear All
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-64 overflow-y-auto border border-gray-100 rounded-md p-3">
                {segments.map((segment) => (
                  <div
                    key={segment.id}
                    className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                      selectedSegments.includes(segment.id)
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => handleSegmentToggle(segment.id)}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-gray-900">
                          {segment.name}
                        </h4>
                        <p className="text-sm text-gray-500 line-clamp-2 leading-tight mt-1" title={segment.description}>
                          {segment.description}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {segment.active_leads} active leads
                        </p>
                      </div>
                      {selectedSegments.includes(segment.id) && (
                        <CheckIcon className="h-5 w-5 text-indigo-600 flex-shrink-0" />
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {selectedSegments.length > 0 && (
                <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>{totalRecipients}</strong> total recipients across{' '}
                    <strong>{selectedSegments.length}</strong> segments
                  </p>
                </div>
              )}

              {/* Segment Compatibility Warning */}
              {campaignMode === 'single' && selectedSegments.length > 1 && (
                <div className="mt-3">
                  {checkingCompatibility ? (
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600 mr-2"></div>
                        <p className="text-sm text-gray-600">Checking segment compatibility...</p>
                      </div>
                    </div>
                  ) : segmentCompatibility && (
                    <div className={`p-4 rounded-lg border ${
                      segmentCompatibility.safetyLevel === 'safe' 
                        ? 'bg-green-50 border-green-200' 
                        : segmentCompatibility.safetyLevel === 'warning'
                        ? 'bg-yellow-50 border-yellow-300'
                        : 'bg-red-50 border-red-300'
                    }`}>
                      <div className="flex items-start">
                        <div className={`flex-shrink-0 ${
                          segmentCompatibility.safetyLevel === 'safe' 
                            ? 'text-green-500' 
                            : segmentCompatibility.safetyLevel === 'warning'
                            ? 'text-yellow-600'
                            : 'text-red-500'
                        }`}>
                          {segmentCompatibility.safetyLevel === 'safe' 
                            ? '✅' 
                            : segmentCompatibility.safetyLevel === 'warning'
                            ? '⚠️'
                            : '🚨'
                          }
                        </div>
                        <div className="ml-3 flex-1">
                          <h4 className={`text-sm font-medium ${
                            segmentCompatibility.safetyLevel === 'safe' 
                              ? 'text-green-800' 
                              : segmentCompatibility.safetyLevel === 'warning'
                              ? 'text-yellow-800'
                              : 'text-red-800'
                          }`}>
                            {segmentCompatibility.compatible 
                              ? 'Segments Compatible' 
                              : segmentCompatibility.safetyLevel === 'warning'
                              ? 'Minor Conflicts Detected'
                              : 'Significant Conflicts Detected'
                            }
                          </h4>
                          <div className={`mt-2 text-sm ${
                            segmentCompatibility.safetyLevel === 'safe' 
                              ? 'text-green-700' 
                              : segmentCompatibility.safetyLevel === 'warning'
                              ? 'text-yellow-700'
                              : 'text-red-700'
                          }`}>
                            <p className="mb-2">{segmentCompatibility.recommendation}</p>
                            {segmentCompatibility.conflicts.length > 0 && (
                              <div>
                                <p className="font-medium mb-1">Conflicts found:</p>
                                <ul className="list-disc list-inside space-y-1">
                                  {segmentCompatibility.conflicts.map((conflict, index) => (
                                    <li key={index} className="text-xs">{conflict}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                          {segmentCompatibility.safetyLevel === 'risky' && (
                            <div className="mt-3">
                              <button
                                onClick={() => setCampaignMode('individual')}
                                className="inline-flex items-center px-3 py-1.5 border border-red-300 text-xs font-medium rounded text-red-700 bg-red-50 hover:bg-red-100"
                              >
                                Switch to Individual Campaign Mode
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Template Selection */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">Generation Method</h4>
              <div className="space-y-4">
                {/* Method Selection */}
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="generationMethod"
                      checked={useTemplate}
                      onChange={() => setUseTemplate(true)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      <strong>Use Template</strong> - Generate consistent, branded emails with professional layouts
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="generationMethod"
                      checked={!useTemplate}
                      onChange={() => setUseTemplate(false)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      <strong>Generate from Scratch</strong> - AI creates the full email structure and design
                    </span>
                  </label>
                </div>

                {/* Template Selection UI */}
                {useTemplate && (
                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Template Category (optional)
                      </label>
                      <select
                        value={templateCategory}
                        onChange={(e) => setTemplateCategory(e.target.value)}
                        className="w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      >
                        <option value="">All Categories</option>
                        <option value="newsletter">Newsletter</option>
                        <option value="welcome">Welcome</option>
                        <option value="resources">Resources</option>
                        <option value="events">Events</option>
                      </select>
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Template ({availableTemplates.length} available)
                      </label>
                      
                      {availableTemplates.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-48 overflow-y-auto border border-gray-100 rounded-md p-2">
                          {availableTemplates.map((template) => (
                            <div
                              key={template.id}
                              className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                                selectedTemplate === template.id
                                  ? 'border-indigo-500 bg-indigo-50'
                                  : 'border-gray-200 hover:border-gray-300 bg-white'
                              }`}
                              onClick={() => setSelectedTemplate(template.id)}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-2 mb-1">
                                    <h5 className="text-sm font-medium text-gray-900">
                                      {template.name}
                                    </h5>
                                    <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                                      {template.category}
                                    </span>
                                  </div>
                                  <p className="text-xs text-gray-600 line-clamp-2">
                                    {template.description}
                                  </p>
                                  <p className="text-xs text-gray-400 mt-1">
                                    Used {template.usage_count} times
                                  </p>
                                </div>
                                <div className="flex flex-col items-center space-y-1 ml-2">
                                  {selectedTemplate === template.id && (
                                    <CheckIcon className="h-4 w-4 text-indigo-600" />
                                  )}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleTemplatePreview(template.id);
                                    }}
                                    className="p-1 text-gray-400 hover:text-gray-600"
                                    title="Preview template"
                                  >
                                    <EyeIcon className="h-3 w-3" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-6 text-gray-500">
                          <DocumentTextIcon className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                          <p className="text-sm">No templates available</p>
                          <p className="text-xs text-gray-400">
                            {templateCategory ? 'Try selecting a different category' : 'Create templates in the Templates section'}
                          </p>
                        </div>
                      )}
                    </div>

                    {selectedTemplate && (
                      <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                        <div className="flex items-start">
                          <CheckIcon className="h-4 w-4 text-blue-500 mr-2 mt-0.5" />
                          <div className="text-xs text-blue-700">
                            <p className="font-medium">Template-based generation selected</p>
                            <p>AI will generate content blocks to fill the selected template, ensuring consistent formatting and branding.</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Legacy Generation Notice */}
                {!useTemplate && (
                  <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                    <div className="flex items-start">
                      <SparklesIcon className="h-4 w-4 text-amber-500 mr-2 mt-0.5" />
                      <div className="text-xs text-amber-700">
                        <p className="font-medium">Full HTML generation selected</p>
                        <p>AI will create the complete email structure. This may result in inconsistent formatting compared to template-based generation.</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Content Prompt */}
            <div>
              <label htmlFor="prompt" className="block text-sm font-medium text-gray-700 mb-2">
                Content Topic/Prompt *
              </label>
              <textarea
                id="prompt"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={3}
                className="w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="Describe what you want the newsletter to be about..."
              />
              
              {/* Prompt Suggestions */}
              <div className="mt-3">
                <p className="text-xs text-gray-500 mb-2">Quick suggestions:</p>
                <div className="flex flex-wrap gap-2">
                  {promptSuggestions.slice(0, 6).map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => setPrompt(suggestion)}
                      className="text-xs px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded-full text-gray-700"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Optional Theme */}
            <div>
              <label htmlFor="theme" className="block text-sm font-medium text-gray-700 mb-2">
                Theme (optional)
              </label>
              <input
                id="theme"
                type="text"
                value={theme}
                onChange={(e) => setTheme(e.target.value)}
                className="w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="e.g., 'Back to School', 'Holiday Special', 'New Year Goals'"
              />
            </div>

            {/* Campaign Mode */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">Campaign Strategy</h4>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Campaign Mode
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="campaignMode"
                        value="single"
                        checked={campaignMode === 'single'}
                        onChange={(e) => handleCampaignModeChange(e.target.value as 'single' | 'individual')}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        <strong>Single Campaign</strong> - One campaign sent to multiple segments (1 approval needed)
                      </span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="campaignMode"
                        value="individual"
                        checked={campaignMode === 'individual'}
                        onChange={(e) => handleCampaignModeChange(e.target.value as 'single' | 'individual')}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        <strong>Individual Campaigns</strong> - Separate campaign per segment (multiple approvals)
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Options */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">Content Options</h4>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={useKnowledgeBase}
                    onChange={(e) => setUseKnowledgeBase(e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Use knowledge base for accurate school information
                  </span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={includeResources}
                    onChange={(e) => setIncludeResources(e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Include relevant resource recommendations
                  </span>
                </label>
              </div>
            </div>

            {/* Voice Settings Preview */}
            {voiceSettings && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Voice & Tone</h4>
                <div className="bg-gray-50 rounded-lg p-4 text-sm">
                  <p><strong>Tone:</strong> {voiceSettings.tone}</p>
                  <p><strong>Style:</strong> {voiceSettings.style}</p>
                  <p><strong>Length:</strong> {voiceSettings.preferred_length}</p>
                </div>
              </div>
            )}
          </div>
          </div>
        </div>

        <div className="flex items-center justify-between p-6 bg-gray-50 border-t border-gray-200 flex-shrink-0">
          <div className="text-sm text-gray-500">
            {selectedSegments.length > 0 && (
              <>
                {campaignMode === 'single' 
                  ? `Will generate 1 campaign for ${selectedSegments.length} segments (${totalRecipients} recipients)`
                  : `Will generate ${selectedSegments.length} campaigns (${totalRecipients} total recipients)`
                }
              </>
            )}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleGenerate}
              disabled={generating || selectedSegments.length === 0 || !prompt.trim() || (useTemplate && !selectedTemplate)}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {generating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Generating...
                </>
              ) : (
                <>
                  <SparklesIcon className="h-4 w-4 mr-2" />
                  Generate Content
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Template Preview Modal */}
      {showTemplatePreview && previewTemplate && (
        <TemplatePreview
          template={previewTemplate}
          onClose={() => setShowTemplatePreview(false)}
          onEdit={(template) => {
            // Close preview and potentially navigate to template editor
            setShowTemplatePreview(false);
            console.log('Edit template:', template.id);
            // Could integrate with template management if needed
          }}
        />
      )}
    </div>
  );
};

export default ContentGenerator;